import { useState, useEffect } from 'react';
import { assetsAPI } from '../../services/api';

export const timeframeConfig = {
  '1D': { days: 1, label: '1 Day' },
  '1W': { days: 7, label: '1 Week' },
  '1M': { days: 30, label: '1 Month' },
  '3M': { days: 90, label: '3 Months' },
  '6M': { days: 180, label: '6 Months' },
  '1Y': { days: 365, label: '1 Year' },
};

export const intervalConfig = {
  '1m': { minutes: 1, label: '1 Minute' },
  '5m': { minutes: 5, label: '5 Minutes' },
  '15m': { minutes: 15, label: '15 Minutes' },
  '30m': { minutes: 30, label: '30 Minutes' },
  '1h': { minutes: 60, label: '1 Hour' },
  '4h': { minutes: 240, label: '4 Hours' },
  '1d': { minutes: 1440, label: '1 Day' },
};

/**
 * @param {string} symbol
 * @param {string} interval - bar interval (1m, 5m, etc.)
 * @param {string} timeframe - lookback window (1D, 1W, 1M, etc.)
 * @param {object} [opts]
 * @param {number} [opts.refreshInterval=300000] - ms between refreshes (0 to disable). Default 5 min.
 */
export function useOHLCData(symbol, interval = '5m', timeframe = '1M', opts = {}) {
  const refreshInterval = opts.refreshInterval ?? 300000; // 5 minutes
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPrice, setCurrentPrice] = useState(null);

  useEffect(() => {
    if (!symbol) {
      setData([]);
      setCurrentPrice(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timer = null;

    const fetchBars = async (isInitial) => {
      if (isInitial) setLoading(true);
      try {
        const tfConfig = timeframeConfig[timeframe];
        const intConfig = intervalConfig[interval];
        if (!tfConfig || !intConfig) return;

        const totalMinutes = tfConfig.days * 24 * 60;
        const barCount = Math.min(Math.floor(totalMinutes / intConfig.minutes), 1000);

        const res = await assetsAPI.bars(symbol, interval, barCount);
        const bars = res.data || [];

        if (cancelled) return;

        if (bars.length === 0) {
          if (isInitial) {
            setData([]);
            setCurrentPrice(null);
          }
          return;
        }

        const formatted = bars.map((bar) => ({
          date: new Date(bar.timestamp),
          open: parseFloat(bar.open),
          high: parseFloat(bar.high),
          low: parseFloat(bar.low),
          close: parseFloat(bar.close),
          volume: parseFloat(bar.volume || 0),
        })).filter((d) =>
          d.date instanceof Date && !isNaN(d.date) &&
          isFinite(d.open) && isFinite(d.high) && isFinite(d.low) && isFinite(d.close)
        );

        setData(formatted);
        setCurrentPrice(formatted[formatted.length - 1]?.close ?? null);
      } catch (error) {
        console.error('Error fetching OHLC data:', error);
        if (!cancelled && isInitial) {
          setData([]);
          setCurrentPrice(null);
        }
      } finally {
        if (!cancelled && isInitial) setLoading(false);
      }
    };

    fetchBars(true);

    // Poll for new bars
    if (refreshInterval > 0) {
      timer = setInterval(() => fetchBars(false), refreshInterval);
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [symbol, interval, timeframe, refreshInterval]);

  return { data, loading, currentPrice, setData, setCurrentPrice };
}
